import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';
import { gammeFromSku, gridUnitCents, nextThresholdFor } from '@theme/group-order-pricing';

/**
 * <group-order-composer> — tunnel « Commande groupée ».
 *
 * Un design maître par modèle (créé dans l'éditeur), décliné ligne par ligne
 * dans un tableau (champs texte du template + taille + quantité). Le brouillon
 * vit en localStorage ET côté backend (uuid = code/lien de reprise). À l'ajout
 * au panier, le backend matérialise chaque ligne en un vrai design (designId)
 * → le pipeline atelier/expédition reste inchangé.
 */

const DRAFT_KEY = 'lfc-group-order:v1';
const DRAFT_TTL_MS = 30 * 24 * 3600 * 1000;
const SAVE_DEBOUNCE_MS = 1500;
// Le backend accepte 100 lignes par appel (concurrence 3) : 20 par lot garde
// une progression visible sans multiplier les requêtes (rate limit).
const MATERIALIZE_CHUNK = 20;
// Une ligne = des paires identiques (même texte, même taille) : une entreprise
// commande 120 paires en 4 lignes, pas en 3 lignes de 50 + 1 de 20.
const MAX_QTY_PER_ROW = 500;
const MAX_ROWS = 400;
const ID_PATTERN = /^[a-z0-9-]{1,12}$/i;

const newId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`).slice(0, 8);

// Copie serveur/thème : les emojis ne s'impriment pas (rendu node-canvas),
// même filtre que l'éditeur /use. Renvoie la valeur nettoyée et un booléen
// « quelque chose a été retiré » pour prévenir l'utilisateur.
const EMOJI_RE = /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|[\u{1F3FB}-\u{1F3FF}]|[\u{FE00}-\u{FE0F}]|\u{200D}|\u{20E3}/gu;
const sanitizeText = (value) => {
  const cleaned = String(value ?? '')
    .replace(EMOJI_RE, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, 40);
  return { cleaned, stripped: cleaned.trim() !== String(value ?? '').trim() };
};

class GroupOrderComposer extends HTMLElement {
  connectedCallback() {
    // Overrides localStorage pour le dev local : backend (API) et éditeur (client /use)
    this.editorBase = localStorage.getItem('lfc-editor-override') || this.dataset.editorBase;
    this.backendBase = localStorage.getItem('lfc-backend-override') || this.dataset.editorBase;

    this.refs = {};
    for (const element of this.querySelectorAll('[data-ref]')) {
      this.refs[element.dataset.ref] = element;
    }

    this.cards = new Map();
    for (const card of this.querySelectorAll('[data-ref="coffret-card"]')) {
      const data = JSON.parse(card.querySelector('[data-ref="coffret-card-data"]').textContent);
      this.cards.set(String(data.productId), { element: card, data });
    }

    this.fieldsCache = new Map();
    this.fieldsFailed = new Set();
    this.thumbsCache = new Map();
    this.masterTexts = new Map();
    this.busy = false;
    this.draft = this.#loadLocalDraft();
    // Étape visible dès le premier rendu (sinon flash du choix de modèle
    // pendant le chargement du brouillon et des champs).
    this.dataset.step = this.draft.step;
    this.#initCustomGammeSelect();
    this.#bind();
    this.#boot();

    // Retour depuis l'éditeur via le bouton Précédent du navigateur : page
    // restaurée depuis le bfcache sans rechargement, on relit l'état.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.draft = this.#loadLocalDraft();
        this.#boot();
      }
    });
    // Sauvegarde en attente (débounce) quand l'onglet se ferme ou passe en
    // arrière-plan : envoi immédiat avec keepalive.
    window.addEventListener('pagehide', () => this.#flushBackendSave());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.#flushBackendSave();
    });
  }

  /**
   * Sélecteur de gamme du parcours sur mesure : la gamme détermine la variante
   * (SKU → grille de prix), on la demande donc avant la personnalisation quand
   * le produit support en propose plusieurs.
   */
  #initCustomGammeSelect() {
    const select = this.refs['custom-template-gamme'];
    const entry = this.#customProductEntry();
    const gammes = entry?.data.gammeValues ?? [];
    if (!select || gammes.length <= 1) return;
    select.innerHTML =
      '<option value="" selected disabled>Gamme…</option>' +
      gammes.map((g) => `<option value="${this.#escape(g)}">${this.#escape(g)}</option>`).join('');
    select.hidden = false;
  }

  #customProductEntry() {
    const card = this.querySelector('[data-ref="custom-product-card"] [data-ref="coffret-card"]');
    return card ? (this.cards.get(card.dataset.productId) ?? null) : null;
  }

  async #boot() {
    const params = new URLSearchParams(window.location.search);
    const resumeId = params.get('go');

    if (resumeId && resumeId !== this.draft.groupOrderId) {
      await this.#resumeFromBackend(resumeId);
    }
    this.#consumeEditorReturn();
    await this.#ensureFieldsLoaded();
    this.#render();
  }

  // ——————————————————————————————————————————————— état ———

  #loadLocalDraft() {
    try {
      const raw = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (raw && raw.version === 1 && Date.now() - raw.updatedAt < DRAFT_TTL_MS) {
        return this.#normalizeState({ ...this.#freshDraft(), ...raw });
      }
    } catch {
      /* brouillon corrompu */
    }
    return this.#freshDraft();
  }

  #freshDraft() {
    return {
      version: 1,
      groupOrderId: null,
      step: 'choose',
      models: [],
      activeModelId: null,
      rows: [],
      pending: null,
      updatedAt: Date.now(),
    };
  }

  #save() {
    this.draft.updatedAt = Date.now();
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(this.draft));
    } catch {
      /* stockage local indisponible */
    }
    this.#scheduleBackendSave();
  }

  #scheduleBackendSave() {
    if (!this.draft.groupOrderId) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.#pushBackendDraft();
    }, SAVE_DEBOUNCE_MS);
  }

  #flushBackendSave() {
    if (!this.saveTimer) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.#pushBackendDraft({ keepalive: true });
  }

  async #ensureBackendDraft() {
    if (this.draft.groupOrderId) return this.draft.groupOrderId;
    const response = await fetch(`${this.backendBase}/group-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: this.#backendState() }),
    });
    if (!response.ok) throw new Error('création du brouillon impossible');
    const { id } = await response.json();
    this.draft.groupOrderId = id;
    this.#save();
    return id;
  }

  #backendState() {
    const { version, groupOrderId, ...state } = this.draft;
    return state;
  }

  /**
   * Pousse le brouillon au backend. Renvoie true si la sauvegarde est
   * confirmée ; `throwOnError` pour les parcours où l'utilisateur attend une
   * confirmation (« Sauvegarder pour plus tard » ne doit jamais afficher
   * « sauvegardé » sur un échec silencieux).
   */
  async #pushBackendDraft({ status = null, throwOnError = false, keepalive = false } = {}) {
    if (!this.draft.groupOrderId) return false;
    try {
      const response = await fetch(`${this.backendBase}/group-order/${this.draft.groupOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        keepalive,
        body: JSON.stringify({ state: this.#backendState(), ...(status ? { status } : {}) }),
      });
      if (response.status === 409) {
        // Commande déjà payée côté Shopify : ce brouillon est terminé.
        this.#handleAlreadyOrdered(await response.json().catch(() => ({})));
        return false;
      }
      if (!response.ok) throw new Error('sauvegarde impossible');
      return true;
    } catch (error) {
      if (throwOnError) throw error;
      return false;
    }
  }

  #handleAlreadyOrdered(payload = {}) {
    const name = payload.shopifyOrderName ? ` (commande ${payload.shopifyOrderName})` : '';
    localStorage.removeItem(DRAFT_KEY);
    this.draft = this.#freshDraft();
    const url = new URL(window.location.href);
    url.searchParams.delete('go');
    history.replaceState({}, '', url.toString());
    this.#render();
    this.#showError(`Cette commande groupée a déjà été passée${name} : tu peux en démarrer une nouvelle.`, 12000);
  }

  /**
   * Reprise par code/lien (`?go=`). Chaque échec est dit à l'utilisateur :
   * avant, un code faux ou une commande déjà passée affichait le choix des
   * modèles sans un mot.
   */
  async #resumeFromBackend(id) {
    let response;
    try {
      response = await fetch(`${this.backendBase}/group-order/${id}`);
    } catch {
      this.#showError('Reprise impossible pour le moment (connexion) : réessaie dans un instant.');
      return;
    }
    const dropGo = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('go');
      history.replaceState({}, '', url.toString());
    };
    if (!response.ok) {
      dropGo();
      this.#showError(
        response.status === 404 || response.status === 400
          ? 'Code de reprise introuvable : vérifie le code ou le lien reçu par mail.'
          : 'Reprise impossible pour le moment : réessaie dans un instant.',
        10000,
      );
      return;
    }
    const data = await response.json();
    if (data.status === 'ordered') {
      dropGo();
      const name = data.shopifyOrderName ? ` (commande ${data.shopifyOrderName})` : '';
      this.#showError(`Cette commande groupée a déjà été passée${name} : tu peux en démarrer une nouvelle.`, 12000);
      return;
    }
    this.draft = this.#normalizeState({ ...this.#freshDraft(), ...data.state, groupOrderId: id });
    if (data.status === 'in_cart') {
      // Les lignes sont encore au panier : on les retire pour éviter un doublon
      // au prochain ajout (même mécanique que « Modifier ma commande »).
      await this.#removeGroupFromCart(id);
      this.#showError('Commande retrouvée : ses paires ont été retirées du panier, elles y reviendront quand tu auras terminé.', 9000);
    }
    this.#save();
  }

  async #removeGroupFromCart(groupOrderId) {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const updates = {};
      for (const item of cart.items ?? []) {
        if (item.properties?._groupOrderId === groupOrderId) updates[item.key] = 0;
      }
      if (!Object.keys(updates).length) return;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (response.ok) {
        const updated = await response.json();
        document.dispatchEvent(
          new CartUpdatedEvent({}, { itemCount: updated.item_count, source: 'group-order', sections: null }),
        );
      }
    } catch {
      /* panier injoignable : le doublon éventuel sera visible au panier */
    }
  }

  /**
   * Un brouillon partagé par lien est une donnée EXTERNE : on ne garde que
   * des identifiants sûrs, des quantités bornées et des textes courts (les
   * identifiants sont injectés dans le HTML du tableau).
   */
  #normalizeState(state) {
    const safeId = (value) => (typeof value === 'string' && ID_PATTERN.test(value) ? value : newId());
    const models = (Array.isArray(state.models) ? state.models : []).slice(0, 20).map((model) => ({
      modelId: safeId(model?.modelId),
      productId: String(model?.productId ?? ''),
      handle: String(model?.handle ?? ''),
      title: String(model?.title ?? '').slice(0, 120),
      image: typeof model?.image === 'string' ? model.image.slice(0, 500) : '',
      templateId: model?.templateId ? String(model.templateId).slice(0, 12) : null,
      customizable: Boolean(model?.customizable),
      gamme: typeof model?.gamme === 'string' ? model.gamme.slice(0, 40) : null,
      masterDesignId: typeof model?.masterDesignId === 'string' ? model.masterDesignId.slice(0, 40) : null,
      masterThumb: typeof model?.masterThumb === 'string' ? model.masterThumb.slice(0, 500) : null,
      mode: model?.mode === 'generic' || model?.mode === 'perPair' ? model.mode : null,
    }));
    const modelIds = new Set(models.map((m) => m.modelId));
    const rows = (Array.isArray(state.rows) ? state.rows : [])
      .filter((row) => row && modelIds.has(row.modelId))
      .slice(0, MAX_ROWS)
      .map((row) => {
        const values = {};
        for (const [key, value] of Object.entries(row.values ?? {}).slice(0, 20)) {
          if (typeof key === 'string' && key.length <= 128) values[key] = sanitizeText(value).cleaned;
        }
        return {
          rowId: safeId(row.rowId),
          modelId: row.modelId,
          values,
          taille: typeof row.taille === 'string' ? row.taille.slice(0, 40) : null,
          qty: Math.max(1, Math.min(MAX_QTY_PER_ROW, parseInt(row.qty, 10) || 1)),
          designId: typeof row.designId === 'string' ? row.designId.slice(0, 40) : null,
        };
      });
    return {
      ...state,
      models,
      rows,
      activeModelId: modelIds.has(state.activeModelId) ? state.activeModelId : (models[0]?.modelId ?? null),
      step: state.step === 'table' && models.length ? 'table' : 'choose',
      pending: state.pending && modelIds.has(state.pending.modelId) ? { modelId: state.pending.modelId } : null,
    };
  }

  // ——————————————————————————————————————————————— éditeur ———

  #consumeEditorReturn() {
    const params = new URLSearchParams(window.location.search);
    const designId = params.get('d');
    const thumb = params.get('thumb');

    if (params.has('d') || params.has('thumb')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('d');
      url.searchParams.delete('thumb');
      if (this.draft.groupOrderId) url.searchParams.set('go', this.draft.groupOrderId);
      history.replaceState({}, '', url.toString());
    }

    if (!designId && this.draft.pending) {
      // Retour de l'éditeur sans design (Précédent, fermeture) : le modèle
      // poussé avant le départ n'a pas de design de base. Sans ligne, on le
      // retire ; sinon il reste visible avec le badge « design à créer ».
      const ghost = this.draft.models.find((m) => m.modelId === this.draft.pending.modelId);
      if (ghost && !ghost.masterDesignId && !this.draft.rows.some((r) => r.modelId === ghost.modelId)) {
        this.draft.models = this.draft.models.filter((m) => m.modelId !== ghost.modelId);
        if (this.draft.activeModelId === ghost.modelId) this.draft.activeModelId = this.draft.models[0]?.modelId ?? null;
        if (!this.draft.models.length) this.draft.step = 'choose';
      }
      this.draft.pending = null;
      this.#save();
      return;
    }
    if (!designId) return;

    if (!this.draft.pending) {
      // Design terminé mais brouillon local perdu (webview, stockage vidé) :
      // on le rattache au dernier modèle personnalisable sans design.
      const orphanTarget = [...this.draft.models].reverse().find((m) => m.customizable && !m.masterDesignId);
      if (!orphanTarget) {
        this.#showError('Ton design est enregistré mais nous ne retrouvons plus le modèle associé : choisis-le à nouveau.', 10000);
        return;
      }
      this.draft.pending = { modelId: orphanTarget.modelId };
    }

    const model = this.draft.models.find((m) => m.modelId === this.draft.pending.modelId);
    if (model) {
      this.thumbsCache?.delete(designId); // design retouché : vignettes à recharger
      const firstDesign = !model.masterDesignId;
      model.masterDesignId = designId;
      if (firstDesign) model.mode = null; // première personnalisation : on demande le type de commande
      if (thumb) model.masterThumb = thumb;
      // Le design maître est modifié EN PLACE (même id) : les lignes déjà
      // déclinées pointent sur l'ancienne version, elles doivent être refaites.
      for (const row of this.draft.rows) {
        if (row.modelId === model.modelId) row.designId = null;
      }
      this.draft.activeModelId = model.modelId;
      this.draft.step = 'table';
      if (!this.draft.rows.some((row) => row.modelId === model.modelId)) {
        this.#addRow(model.modelId);
      }
    }
    this.draft.pending = null;
    this.#save();
  }

  async #startMasterPersonalization(model) {
    this.draft.pending = { modelId: model.modelId };
    this.#save();
    try {
      await this.#ensureBackendDraft();
      await this.#pushBackendDraft();
    } catch {
      /* le brouillon local suffit pour survivre au round-trip */
    }
    const origin = new URL(window.location.href);
    origin.searchParams.delete('d');
    origin.searchParams.delete('thumb');
    if (this.draft.groupOrderId) origin.searchParams.set('go', this.draft.groupOrderId);
    const target = new URL(`${this.editorBase}/use/${model.templateId}`);
    target.searchParams.set('origin', origin.toString());
    if (model.masterDesignId) target.searchParams.set('d', model.masterDesignId);
    window.location.href = target.toString();
  }

  // ——————————————————————————————————————————————— modèles & lignes ———

  #chooseModel(cardData, gamme = null) {
    const model = {
      modelId: newId(),
      productId: String(cardData.productId),
      handle: cardData.handle,
      title: cardData.title,
      image: cardData.image,
      templateId: cardData.templateId,
      customizable: cardData.customizable,
      gamme: gamme ?? cardData.gammeValues?.[0] ?? null,
      masterDesignId: null,
      masterThumb: null,
    };
    this.draft.models.push(model);
    this.draft.activeModelId = model.modelId;

    if (model.customizable && model.templateId) {
      this.#startMasterPersonalization(model);
      return;
    }
    this.draft.step = 'table';
    this.#addRow(model.modelId);
    this.#save();
    this.#render();
  }

  /**
   * Modèle sur mesure : valide le templateId saisi (endpoint des champs, qui
   * remplit aussi le cache) puis démarre le flow classique en surchargeant le
   * templateId du produit support configuré dans la section.
   */
  async #chooseCustomTemplate() {
    const errorElement = this.refs['custom-template-error'];
    errorElement.hidden = true;
    const templateId = parseInt(this.refs['custom-template-id'].value.trim(), 10);
    const showError = (message) => {
      errorElement.textContent = message;
      errorElement.hidden = false;
    };
    if (!Number.isInteger(templateId) || templateId <= 0) {
      showError('Saisis l’identifiant numérique transmis par notre équipe.');
      return;
    }
    const entry = this.#customProductEntry();
    if (!entry) {
      showError('Option sur mesure indisponible pour le moment.');
      return;
    }
    let gamme = null;
    if ((entry.data.gammeValues ?? []).length > 1) {
      gamme = this.refs['custom-template-gamme'].value || null;
      if (!gamme) {
        showError('Choisis la gamme de ton modèle sur mesure.');
        return;
      }
    }

    const button = this.refs['custom-template-go'];
    button.disabled = true;
    try {
      const response = await fetch(`${this.backendBase}/group-order/fields/${templateId}`);
      if (!response.ok) {
        showError('Modèle introuvable : vérifie l’identifiant transmis par notre équipe.');
        return;
      }
      const data = await response.json();
      this.fieldsCache.set(templateId, data.fields ?? []);
      this.#chooseModel({ ...entry.data, templateId, customizable: true }, gamme);
    } catch {
      showError('Vérification impossible pour le moment : réessaie dans un instant.');
    } finally {
      button.disabled = false;
    }
  }

  #removeModel(modelId) {
    if (!confirm('Retirer ce modèle et toutes ses lignes ?')) return;
    this.draft.models = this.draft.models.filter((m) => m.modelId !== modelId);
    this.draft.rows = this.draft.rows.filter((r) => r.modelId !== modelId);
    if (this.draft.activeModelId === modelId) {
      this.draft.activeModelId = this.draft.models[0]?.modelId ?? null;
    }
    if (!this.draft.models.length) this.draft.step = 'choose';
    this.#save();
    this.#render();
  }

  /**
   * Nouvelle ligne. `from` = ligne source (duplication : textes + taille),
   * `after` = insérer juste après cette ligne (Entrée dans le tableau) au lieu
   * d'aller en fin de liste ; `inheritTaille` = reprendre la taille de la ligne
   * courante (une équipe saisit souvent plusieurs membres de même pointure).
   */
  #addRow(modelId, from = null, { after = null, inheritTaille = null } = {}) {
    if (this.draft.rows.length >= MAX_ROWS) {
      this.#showError(`${MAX_ROWS} lignes maximum : utilise les quantités pour les paires identiques.`);
      return null;
    }
    const row = {
      rowId: newId(),
      modelId,
      values: from ? { ...from.values } : {},
      taille: from?.taille ?? inheritTaille ?? null,
      qty: 1,
      designId: null,
    };
    const index = after ? this.draft.rows.findIndex((r) => r.rowId === after.rowId) : -1;
    if (index >= 0) this.draft.rows.splice(index + 1, 0, row);
    else this.draft.rows.push(row);
    return row;
  }

  /** Ajoute une ligne vide pour le modèle actif (après `after` si fournie) et met le focus dessus. */
  #addEmptyRow(after = null) {
    if (this.busy) return;
    const model = this.#activeModel();
    if (!model) return;
    const row = this.#addRow(model.modelId, null, { after, inheritTaille: after?.taille ?? null });
    if (!row) return;
    this.#save();
    this.#renderTable();
    this.#renderSummary();
    this.#focusRow(row.rowId);
  }

  #focusRow(rowId) {
    const cell = this.querySelector(`[data-row-id="${this.#escape(rowId)}"]`);
    const target = cell?.querySelector('input[type="text"]') ?? cell?.querySelector('select');
    target?.focus();
    cell?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Une ligne par taille disponible (scénario « même personnalisation pour tout le monde »). */
  #addRowPerSize() {
    if (this.busy) return;
    const model = this.#activeModel();
    if (!model) return;
    const card = this.cards.get(model.productId);
    const tailles = card?.data.tailleValues ?? [];
    if (!tailles.length) return;
    const existing = new Set(this.draft.rows.filter((r) => r.modelId === model.modelId && r.taille).map((r) => r.taille));
    let first = null;
    for (const taille of tailles) {
      if (existing.has(taille)) continue;
      const row = this.#addRow(model.modelId, null, { inheritTaille: taille });
      if (!row) break;
      first = first ?? row;
    }
    // Ligne vide initiale (sans taille) devenue inutile : on la retire
    this.draft.rows = this.draft.rows.filter(
      (r) => !(r.modelId === model.modelId && !r.taille && !Object.values(r.values).some(Boolean) && r.qty === 1 && first),
    );
    this.#save();
    this.#renderTable();
    this.#renderSummary();
    if (first) this.querySelector(`[data-row-id="${this.#escape(first.rowId)}"] input[data-kind="qty"]`)?.focus();
  }

  /**
   * Champs texte des modèles. Un échec réseau n'est JAMAIS mis en cache comme
   * « aucun champ » : le tableau afficherait alors des lignes sans prénom et
   * 25 designs identiques partiraient au panier. On garde l'échec en mémoire,
   * on l'affiche, et l'ajout panier reste bloqué tant que les champs manquent.
   */
  async #ensureFieldsLoaded() {
    const templateIds = [
      ...new Set(this.draft.models.filter((m) => m.templateId).map((m) => String(m.templateId))),
    ];
    await Promise.all(
      templateIds.map(async (templateId) => {
        if (this.fieldsCache.has(templateId)) return;
        try {
          const response = await fetch(`${this.backendBase}/group-order/fields/${templateId}`);
          if (response.status === 404) {
            // Modèle supprimé côté LFC : pas de champ, mais pas d'erreur réseau
            this.fieldsCache.set(templateId, []);
            return;
          }
          if (!response.ok) throw new Error(`fields ${response.status}`);
          const data = await response.json();
          this.fieldsCache.set(templateId, data.fields ?? []);
          this.fieldsFailed.delete(templateId);
        } catch {
          this.fieldsFailed.add(templateId);
        }
      }),
    );
    if (this.fieldsFailed.size) {
      this.#showError('Les champs à personnaliser n’ont pas pu être chargés : vérifie ta connexion puis réessaie.', 12000);
    }
  }

  /** Clé de saisie d'un champ (objectId, ou objectId@faceId pour un texte présent sur les deux pieds). */
  #fieldKey(field) {
    return field.key ?? field.objectId;
  }

  /** Aide d'un champ : le texte actuel du design de base sur ce pied, sinon le libellé. */
  #placeholderFor(model, field) {
    const texts = model?.masterDesignId ? this.masterTexts.get(model.masterDesignId) : null;
    const match = texts?.find((t) => t.objectId === field.objectId && (!field.perFace || t.faceId === field.faceId));
    return match?.text ? `${match.text} (design de base)` : field.label;
  }

  /** Met à jour les aides des champs sans re-rendre le tableau (la saisie en cours n'est pas perdue). */
  #applyPlaceholders(model) {
    if (this.#activeModel()?.modelId !== model.modelId) return;
    const fields = this.#fieldsFor(model);
    for (const input of this.refs['table-body']?.querySelectorAll('input[data-kind="value"]') ?? []) {
      const field = fields.find((f) => this.#fieldKey(f) === input.dataset.field);
      if (field) input.placeholder = this.#placeholderFor(model, field);
    }
  }

  /**
   * Type de commande d'un modèle personnalisable : 'generic' (toutes les
   * paires identiques, on ne demande que tailles et quantités, les textes du
   * design de base sont conservés) ou 'perPair' (une ligne par personne).
   * null = pas encore choisi : le panneau de choix remplace le tableau.
   */
  #needsMode(model) {
    return Boolean(model?.customizable && model?.masterDesignId && this.#fieldsFor(model).length && !model.mode);
  }

  #setMode(mode) {
    const model = this.#activeModel();
    if (!model) return;
    model.mode = mode;
    this.#save();
    this.#renderTable();
    this.#renderSummary();
    if (mode) this.refs['table-body']?.querySelector('select, input')?.focus();
  }

  /** Valeurs texte envoyées pour une ligne : aucune en mode générique (textes du design de base). */
  #effectiveValues(model, row) {
    return model?.mode === 'generic' ? {} : (row.values ?? {});
  }

  #fieldsFor(model) {
    return model?.templateId ? (this.fieldsCache.get(String(model.templateId)) ?? []) : [];
  }

  #fieldsMissing(model) {
    return Boolean(model?.customizable && model?.templateId && !this.fieldsCache.has(String(model.templateId)));
  }

  async #retryFields() {
    await this.#ensureFieldsLoaded();
    this.#renderTable();
    this.#renderSummary();
  }

  #activeModel() {
    return this.draft.models.find((m) => m.modelId === this.draft.activeModelId) ?? null;
  }

  // ——————————————————————————————————————————————— prix ———

  #findPairVariant(cardData, taille, gamme) {
    return (cardData.variants ?? []).find((variant) => {
      if (!variant.sku || !variant.sku.startsWith('1-CH-')) return false;
      const options = variant.options ?? [];
      if (!options.includes(taille)) return false;
      if (gamme && cardData.gammeValues?.length > 1 && !options.includes(gamme)) return false;
      return true;
    });
  }

  #totals() {
    let pairs = 0;
    let subtotal = 0;
    let discount = 0;
    let missingPrice = false;
    let belowMin = false;
    let nextThreshold = null;

    const lines = [];
    for (const row of this.draft.rows) {
      const model = this.draft.models.find((m) => m.modelId === row.modelId);
      if (!model) continue;
      pairs += row.qty;
      const card = this.cards.get(model.productId);
      const variant = card && row.taille ? this.#findPairVariant(card.data, row.taille, model.gamme) : null;
      if (!variant) {
        missingPrice = true;
        continue;
      }
      lines.push({ variant, qty: row.qty });
      subtotal += variant.price * row.qty;
    }

    for (const { variant, qty } of lines) {
      const gamme = gammeFromSku(variant.sku);
      const unit = gridUnitCents(gamme, pairs);
      if (unit === null) belowMin = true;
      if (unit !== null && unit < variant.price) discount += (variant.price - unit) * qty;
      const next = nextThresholdFor(gamme, pairs);
      if (next !== null && (nextThreshold === null || next < nextThreshold)) nextThreshold = next;
    }

    return { pairs, subtotal, discount, total: subtotal - discount, missingPrice, belowMin, nextThreshold };
  }

  // ——————————————————————————————————————————————— listeners ———

  #bind() {
    this.refs['sport-filters']?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sport-filter]');
      if (!button) return;
      this.querySelectorAll('[data-sport-filter]').forEach((b) => b.classList.toggle('is-active', b === button));
      this.#applyGridFilters();
    });

    // Arrivée depuis une page club (?club=<handle>) : onglet Clubs ouvert d'office
    this.clubFilter = new URLSearchParams(window.location.search).get('club') || null;
    const clubsTab = this.clubFilter ? this.querySelector('[data-sport-filter="clubs"]') : null;
    if (clubsTab) {
      this.querySelectorAll('[data-sport-filter]').forEach((b) => b.classList.toggle('is-active', b === clubsTab));
      this.#applyGridFilters();
    }

    this.refs['product-search']?.addEventListener('input', () => this.#applyGridFilters());

    this.refs['products-grid']?.addEventListener('click', (event) => {
      const card = event.target.closest('[data-ref="coffret-card"]');
      if (!card) return;
      const entry = this.cards.get(card.dataset.productId);
      if (!entry) return;
      if (
        event.target.closest('[data-ref="coffret-card-personalize"]') ||
        event.target.closest('[data-ref="coffret-card-add"]')
      ) {
        this.#chooseModel(entry.data);
      }
    });

    this.refs['add-model']?.addEventListener('click', () => {
      this.draft.step = 'choose';
      this.#save();
      this.#render();
    });

    this.refs['models-list']?.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-model-id]');
      if (!tab) return;
      if (event.target.closest('.group-order__model-tab-remove')) {
        this.#removeModel(tab.dataset.modelId);
        return;
      }
      const model = this.draft.models.find((m) => m.modelId === tab.dataset.modelId);
      if (!model) return;
      // Modèle personnalisable sans design de base (retour arrière depuis
      // l'éditeur) : on renvoie créer le design plutôt que d'ouvrir un tableau
      // dont les lignes partiraient sans visuel.
      if (model.customizable && model.templateId && !model.masterDesignId) {
        this.draft.activeModelId = model.modelId;
        this.#startMasterPersonalization(model);
        return;
      }
      this.draft.activeModelId = model.modelId;
      this.draft.step = 'table';
      this.#save();
      this.#render();
    });

    this.refs['gamme-select']?.addEventListener('change', () => {
      const model = this.#activeModel();
      if (!model) return;
      model.gamme = this.refs['gamme-select'].value;
      this.#save();
      this.#renderSummary();
    });

    // Nouvelle ligne vide (bouton ou touche Entrée dans le tableau) ; la copie
    // d'une ligne existante reste possible via le bouton dupliquer (⧉).
    this.refs['add-row']?.addEventListener('click', () => this.#addEmptyRow());
    this.refs['add-row-per-size']?.addEventListener('click', () => this.#addRowPerSize());
    // Type de commande, demandé après la première personnalisation d'un modèle
    this.refs['mode-generic']?.addEventListener('click', () => this.#setMode('generic'));
    this.refs['mode-per-pair']?.addEventListener('click', () => this.#setMode('perPair'));
    this.refs['mode-switch']?.addEventListener('click', () => this.#setMode(null));
    this.refs['fields-retry']?.addEventListener('click', () => this.#retryFields());

    this.refs['table-body']?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
      event.preventDefault();
      const cell = event.target.closest('[data-row-id]');
      const current = this.draft.rows.find((r) => r.rowId === cell?.dataset.rowId) ?? null;
      this.#addEmptyRow(current);
    });

    this.refs['edit-master']?.addEventListener('click', () => {
      const model = this.#activeModel();
      if (model?.customizable && !this.busy) this.#startMasterPersonalization(model);
    });

    this.refs['table-body']?.addEventListener('input', (event) => {
      // Pendant l'ajout au panier, le tableau est figé : une frappe aurait
      // remis designId à null sur une ligne déjà déclinée (paire sans design).
      if (this.busy) return;
      const cell = event.target.closest('[data-row-id]');
      if (!cell) return;
      const row = this.draft.rows.find((r) => r.rowId === cell.dataset.rowId);
      if (!row) return;
      const { field, kind } = event.target.dataset;
      if (kind === 'value') {
        const { cleaned, stripped } = sanitizeText(event.target.value);
        row.values[field] = cleaned;
        if (stripped) {
          event.target.value = cleaned;
          this.#showError('Les emojis ne s’impriment pas sur les chaussettes : ils ont été retirés.');
        }
        row.designId = null; // le texte change : la déclinaison précédente est périmée
      }
      if (kind === 'taille') {
        row.taille = event.target.value || null;
        if (row.taille) event.target.classList.remove('is-missing');
      }
      if (kind === 'qty') {
        const parsed = parseInt(event.target.value, 10);
        if (Number.isInteger(parsed)) row.qty = Math.max(1, Math.min(MAX_QTY_PER_ROW, parsed));
      }
      // Taille et quantité ne changent pas le visuel : designId conservé
      this.#save();
      this.#renderSummary();
    });

    // Quantité hors bornes : on réécrit la valeur retenue dans le champ (avant,
    // le champ affichait 120 et le résumé 50 : commande fausse en silence).
    this.refs['table-body']?.addEventListener(
      'change',
      (event) => {
        if (event.target.dataset.kind !== 'qty') return;
        const cell = event.target.closest('[data-row-id]');
        const row = this.draft.rows.find((r) => r.rowId === cell?.dataset.rowId);
        if (!row) return;
        const parsed = parseInt(event.target.value, 10);
        if (!Number.isInteger(parsed) || parsed !== row.qty) {
          event.target.value = row.qty;
          if (Number.isInteger(parsed) && parsed > MAX_QTY_PER_ROW) {
            this.#showError(`${MAX_QTY_PER_ROW} paires maximum par ligne : ajoute une ligne pour le reste.`);
          }
        }
      },
      true,
    );

    this.refs['table-body']?.addEventListener('click', (event) => {
      if (this.busy) return;
      const cell = event.target.closest('[data-row-id]');
      if (!cell) return;
      const row = this.draft.rows.find((r) => r.rowId === cell.dataset.rowId);
      if (!row) return;
      if (event.target.closest('[data-action="duplicate"]')) {
        const copy = this.#addRow(row.modelId, row, { after: row });
        this.#save();
        this.#renderTable();
        this.#renderSummary();
        if (copy) this.#focusRow(copy.rowId);
      }
      if (event.target.closest('[data-action="remove"]')) {
        this.draft.rows = this.draft.rows.filter((r) => r.rowId !== row.rowId);
        this.#save();
        this.#renderTable();
        this.#renderSummary();
      }
    });

    this.refs['save-later']?.addEventListener('click', async () => {
      const button = this.refs['save-later'];
      button.disabled = true;
      try {
        await this.#ensureBackendDraft();
        // Confirmation UNIQUEMENT sur sauvegarde réussie : un 429/500 avalé
        // affichait « Commande sauvegardée » avec un lien vers un brouillon vide.
        const saved = await this.#pushBackendDraft({ throwOnError: true });
        if (!saved) return;
        this.#showResumeBox();
      } catch {
        this.#showError('Sauvegarde impossible pour le moment : réessaie dans un instant.');
      } finally {
        button.disabled = false;
      }
    });

    this.refs['copy-link']?.addEventListener('click', () => {
      navigator.clipboard?.writeText(this.refs['resume-link'].value).catch(() => {});
      this.refs['copy-link'].textContent = 'Copié !';
      setTimeout(() => (this.refs['copy-link'].textContent = 'Copier'), 1600);
    });

    // Lead de relance : email + téléphone → le backend envoie le lien de
    // reprise par mail (boîte assistant) et garde les coordonnées.
    this.refs['lead-submit']?.addEventListener('click', () => this.#submitLead());
    this.refs['lead-phone']?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.#submitLead();
    });

    this.refs['add-to-cart']?.addEventListener('click', () => this.#addToCart());

    // ——— Modèle sur mesure : templateId fourni au client par l'équipe ———
    this.refs['custom-template-toggle']?.addEventListener('click', () => {
      const form = this.refs['custom-template-form'];
      form.hidden = !form.hidden;
      if (!form.hidden) this.refs['custom-template-id'].focus();
    });

    this.refs['custom-template-id']?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.#chooseCustomTemplate();
    });
    this.refs['custom-template-go']?.addEventListener('click', () => this.#chooseCustomTemplate());

    // Bouton grisé : le wrapper capte le clic (pointer-events désactivés sur le
    // bouton disabled) et explique ce qui bloque, en surlignant les manques.
    this.refs['add-to-cart-wrap']?.addEventListener('click', () => {
      if (!this.refs['add-to-cart'].disabled) return;
      const reason = this.#blockingReason();
      if (!reason) return;
      this.#showError(reason);
      this.#highlightMissingTailles();
    });
  }

  #applyGridFilters() {
    const normalize = (value) =>
      (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
    const query = normalize(this.refs['product-search']?.value).trim();
    const active = this.querySelector('[data-sport-filter].is-active')?.dataset.sportFilter;

    // Un onglet = une grille-collection (mêmes collections que le compositeur
    // de pack, triées bestsellers par le cron) : on bascule la grille visible,
    // la recherche filtre dans la grille active.
    for (const grid of this.querySelectorAll('[data-collection-grid]')) {
      const isActive = grid.dataset.collectionGrid === active;
      grid.hidden = !isActive;
      if (!isActive) continue;
      // Onglet Clubs + ?club=<handle> : seulement les paires de ce club, si au moins une existe
      const cards = [...grid.querySelectorAll('[data-ref="coffret-card"]')];
      const clubToken = this.clubFilter && grid.dataset.collectionGrid === 'clubs' ? `,${this.clubFilter},` : null;
      const clubMatch = clubToken && cards.some((card) => (card.dataset.clubs || '').includes(clubToken)) ? clubToken : null;
      for (const card of cards) {
        const entry = this.cards.get(String(card.dataset.productId));
        const title = entry?.data?.title || card.textContent || '';
        const hiddenByClub = Boolean(clubMatch) && !(card.dataset.clubs || '').includes(clubMatch);
        card.hidden = hiddenByClub || (Boolean(query) && !normalize(title).includes(query));
      }
    }
  }

  // ——————————————————————————————————————————————— rendu ———

  #render() {
    this.dataset.step = this.draft.step;
    // La barre reste visible (elle porte le CTA design sur mesure) ; le bouton
    // « + Ajouter un modèle » est inutile quand on est déjà sur le choix.
    this.refs['add-model'].hidden = this.draft.step === 'choose';
    // Titre/sous-titre de page selon l'étape (choix du modèle vs tableau équipe)
    const chooseStep = this.draft.step === 'choose';
    this.refs['page-title'].textContent = chooseStep ? this.dataset.chooseTitle : this.dataset.tableTitle;
    this.refs['group-subtitle'].textContent = chooseStep ? this.dataset.chooseSubtitle : this.dataset.tableSubtitle;
    this.#renderModelsBar();
    if (this.draft.step === 'table') {
      this.#renderTable();
      this.#renderSummary();
    }
  }

  #renderModelsBar() {
    this.refs['models-list'].innerHTML = this.draft.models
      .map((model) => {
        const active = model.modelId === this.draft.activeModelId && this.draft.step === 'table';
        const thumb = model.masterThumb || model.image || '';
        const count = this.draft.rows
          .filter((r) => r.modelId === model.modelId)
          .reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
        const missingDesign = model.customizable && model.templateId && !model.masterDesignId;
        return `
          <button type="button" class="group-order__model-tab${active ? ' is-active' : ''}${missingDesign ? ' is-missing-design' : ''}" data-model-id="${this.#escape(model.modelId)}" title="${missingDesign ? 'Design de base à créer' : ''}">
            ${thumb ? `<img src="${this.#escape(thumb)}" alt="">` : ''}
            <span>${this.#escape(model.title)}${count ? ` · ${this.#escape(count)}` : ''}${missingDesign ? ' · design à créer' : ''}</span>
            <span class="group-order__model-tab-remove" role="button" tabindex="0" aria-label="Retirer ${this.#escape(model.title)}">✕</span>
          </button>`;
      })
      .join('');
  }

  #renderTable() {
    const model = this.#activeModel();
    if (!model) return;
    const card = this.cards.get(model.productId);
    const fields = this.#fieldsFor(model);
    const tailles = card?.data.tailleValues ?? [];
    const gammes = card?.data.gammeValues ?? [];

    this.refs['table-title'].textContent = model.title;
    this.#renderDesignPreview(model);
    this.refs['gamme-wrap'].hidden = gammes.length <= 1;
    if (gammes.length > 1) {
      this.refs['gamme-select'].innerHTML = gammes
        .map((g) => `<option value="${this.#escape(g)}"${g === model.gamme ? ' selected' : ''}>${this.#escape(g)}</option>`)
        .join('');
    }
    this.refs['edit-master'].hidden = !model.customizable;

    const fieldsMissing = this.#fieldsMissing(model);
    if (this.refs['fields-error']) this.refs['fields-error'].hidden = !fieldsMissing;

    // Type de commande : panneau de choix tant qu'il n'est pas fixé ; en mode
    // générique, plus de colonne texte (tailles et quantités seulement)
    const allFields = fields;
    const needsMode = this.#needsMode(model);
    const generic = !allFields.length || model.mode === 'generic';
    const fieldsShown = generic ? [] : allFields;
    const tablePanel = this.querySelector('[data-step-panel="table"]');
    if (this.refs['mode-panel']) this.refs['mode-panel'].hidden = !needsMode;
    tablePanel?.classList.toggle('is-choosing-mode', needsMode);
    if (this.refs['mode-current']) {
      const chosen = allFields.length && model.mode;
      this.refs['mode-current'].hidden = !chosen;
      if (chosen) {
        this.refs['mode-label'].textContent =
          model.mode === 'generic' ? 'Toutes les paires identiques (texte du design de base).' : 'Une personnalisation par paire.';
      }
    }
    if (this.refs['add-row-per-size']) this.refs['add-row-per-size'].hidden = !generic;
    if (this.refs['table-hint']) {
      this.refs['table-hint'].textContent = fieldsShown.length
        ? 'Une ligne par personne : le texte, la taille et le nombre de paires identiques. Laisse le texte vide pour garder celui du design de base.'
        : 'Une ligne par taille avec le nombre de paires : le visuel et le texte sont ceux de ton design de base.';
    }

    // Un champ présent sur les deux pieds = une seule colonne, appliquée aux deux
    // Libellé du backend : nomme déjà le pied quand le texte existe sur les deux
    const fieldLabel = (f) => this.#escape(f.label);
    this.refs['table-head'].innerHTML = `<tr>
      <th class="group-order__col-index" aria-label="Numéro de ligne">#</th>
      ${fieldsShown.map((f) => `<th>${fieldLabel(f)}</th>`).join('')}
      <th>Taille</th><th>Paires</th><th><span class="visually-hidden">Actions</span></th>
    </tr>`;

    const rows = this.draft.rows.filter((r) => r.modelId === model.modelId);
    this.refs['table-body'].innerHTML = rows
      .map(
        (row, index) => `<tr data-row-id="${this.#escape(row.rowId)}"${row.designId ? ' data-materialized="1"' : ''}>
          <td class="group-order__col-index" data-label="Ligne">${index + 1}</td>
          ${fieldsShown
            .map(
              (f) => `<td data-label="${this.#escape(f.label)}"><input type="text" maxlength="40" autocomplete="off" autocapitalize="characters" data-kind="value" data-field="${this.#escape(this.#fieldKey(f))}"
                value="${this.#escape(row.values[this.#fieldKey(f)] ?? '')}" placeholder="${this.#escape(this.#placeholderFor(model, f))}" aria-label="${this.#escape(f.label)}, ligne ${index + 1}"></td>`,
            )
            .join('')}
          <td data-label="Taille"><select data-kind="taille" aria-label="Taille, ligne ${index + 1}">
            <option value=""${row.taille ? '' : ' selected'} disabled>Taille…</option>
            ${tailles.map((t) => `<option value="${this.#escape(t)}"${t === row.taille ? ' selected' : ''}>${this.#escape(t)}</option>`).join('')}
          </select></td>
          <td data-label="Paires"><input type="number" inputmode="numeric" min="1" max="${MAX_QTY_PER_ROW}" data-kind="qty" value="${this.#escape(row.qty)}" aria-label="Nombre de paires, ligne ${index + 1}"></td>
          <td class="group-order__row-actions">
            <button type="button" data-action="duplicate" title="Dupliquer la ligne" aria-label="Dupliquer la ligne ${index + 1}" tabindex="-1">⧉</button>
            <button type="button" data-action="remove" title="Supprimer la ligne" aria-label="Supprimer la ligne ${index + 1}" tabindex="-1">🗑</button>
          </td>
        </tr>`,
      )
      .join('');
    this.refs['table-body'].closest('table')?.classList.toggle('is-busy', this.busy);
  }

  /**
   * Aperçu grand format du design maître : toutes les faces (vignettes du
   * design en base), avec repli sur la vignette de retour d'éditeur puis la
   * photo produit. Chargé une fois par design (cache).
   */
  async #renderDesignPreview(model) {
    const element = this.refs['design-preview'];
    if (!element) return;
    let urls = [];
    if (model.masterDesignId) {
      urls = this.thumbsCache.get(model.masterDesignId);
      if (!urls) {
        try {
          const response = await fetch(`${this.backendBase}/group-order/design/${model.masterDesignId}/thumbnails`);
          const data = response.ok ? await response.json() : {};
          urls = data.thumbnails ?? [];
          // Textes actuels du design de base : aide affichée dans chaque champ
          // (« TEAM » pour le pied gauche, « LOLO » pour le droit)
          this.masterTexts.set(model.masterDesignId, data.texts ?? []);
          this.#applyPlaceholders(model);
        } catch {
          urls = [];
        }
        this.thumbsCache.set(model.masterDesignId, urls);
      }
      // le modèle actif a pu changer pendant le fetch
      if (this.#activeModel()?.modelId !== model.modelId) return;
      if (!urls.length && model.masterThumb) urls = [model.masterThumb];
    } else if (model.image) {
      urls = [model.image];
    }
    element.innerHTML = urls
      .map((url) => `<img src="${this.#escape(url)}" alt="${this.#escape(model.title)}, aperçu du design" loading="lazy">`)
      .join('');
    element.hidden = urls.length === 0;
  }

  #renderSummary() {
    const { pairs, subtotal, discount, total, missingPrice, belowMin, nextThreshold } = this.#totals();
    const ready = !this.busy && this.#blockingReason() === null;
    this.refs['add-to-cart'].disabled = !ready;
    this.refs['add-to-cart'].setAttribute('aria-disabled', String(!ready));

    const lines = [];
    lines.push(`<div class="group-order__summary-line"><span>${pairs} paire${pairs > 1 ? 's' : ''}</span><span>${formatMoney(subtotal)}</span></div>`);
    if (discount > 0) {
      lines.push(
        `<div class="group-order__summary-line group-order__summary-line--discount"><span>Tarif groupe</span><span>−${formatMoney(discount)}</span></div>`,
      );
    }
    // Prix par paire TOUJOURS visible, en gros au niveau du total — c'est
    // l'argument n°1 de la commande groupée (le prix baisse avec le volume).
    if (pairs > 0 && !missingPrice) {
      const unit = Math.round(total / pairs);
      lines.push(
        `<div class="group-order__summary-line group-order__summary-line--unit"><span>Prix par paire</span><span class="group-order__unit-price">${formatMoney(unit)} <small>/ paire</small></span></div>`,
      );
    }
    lines.push(
      `<div class="group-order__summary-line group-order__summary-line--total"><span>Total estimé</span><span>${formatMoney(total)}</span></div>`,
    );
    if (nextThreshold !== null && pairs > 0) {
      const remaining = nextThreshold - pairs;
      const message = belowMin
        ? `Encore ${remaining} paire${remaining > 1 ? 's' : ''} pour débloquer le tarif groupe.`
        : `Encore ${remaining} paire${remaining > 1 ? 's' : ''} pour passer au palier suivant.`;
      lines.push(`<div class="group-order__summary-line"><span style="opacity:.6">${message}</span></div>`);
    }
    if (missingPrice || !this.draft.rows.every((row) => row.taille)) {
      lines.push(`<div class="group-order__summary-line"><span style="opacity:.6">Choisis une taille sur chaque ligne pour valider.</span></div>`);
    }
    this.refs['summary'].innerHTML = lines.join('');
  }

  #showResumeBox() {
    const url = new URL(window.location.href);
    url.searchParams.set('go', this.draft.groupOrderId);
    url.searchParams.delete('d');
    url.searchParams.delete('thumb');
    this.refs['resume-link'].value = url.toString();
    this.refs['resume-code'].textContent = this.draft.groupOrderId;
    // Réarme le formulaire lead à chaque ouverture (nouveau brouillon possible).
    // Le lien/code de reprise n'apparaît qu'après l'envoi du mail (ou en erreur).
    if (this.refs['lead-form']) {
      this.refs['lead-form'].hidden = false;
      this.refs['lead-done'].hidden = true;
      this.refs['lead-error'].hidden = true;
      if (this.refs['resume-box']) this.refs['resume-box'].hidden = true;
    }
    this.refs['popup-modal'].showModal();
  }

  async #submitLead() {
    const email = (this.refs['lead-email']?.value || '').trim();
    const phone = (this.refs['lead-phone']?.value || '').trim();
    const error = this.refs['lead-error'];
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      error.textContent = 'Entre une adresse email valide.';
      error.hidden = false;
      return;
    }
    // Téléphone optionnel : l'email suffit pour recevoir le lien de reprise
    if (phone && phone.length < 6) {
      error.textContent = 'Entre un numéro de téléphone valide, ou laisse le champ vide.';
      error.hidden = false;
      return;
    }
    error.hidden = true;
    const button = this.refs['lead-submit'];
    button.disabled = true;
    button.textContent = 'Envoi…';
    try {
      const response = await fetch(`${this.backendBase}/group-order/${this.draft.groupOrderId}/save-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'envoi impossible');
      this.refs['lead-form'].hidden = true;
      this.refs['lead-done'].hidden = false;
      this.refs['lead-done-message'].textContent = payload.emailSent
        ? `C'est envoyé à ${email} ! Le lien de reprise est dans ta boîte mail.`
        : `C'est noté ! Garde le lien ci-dessous pour reprendre ta commande.`;
      if (this.refs['resume-box']) this.refs['resume-box'].hidden = false;
    } catch {
      error.textContent = 'Envoi impossible pour le moment : garde le lien ci-dessous.';
      error.hidden = false;
      if (this.refs['resume-box']) this.refs['resume-box'].hidden = false;
    } finally {
      button.disabled = false;
      button.textContent = 'Recevoir mon lien par mail';
    }
  }

  /** Raison pour laquelle l'ajout au panier est bloqué, ou null si prêt. */
  #blockingReason() {
    if (!this.draft.rows.length) return 'Ajoute au moins une ligne pour commander.';
    const orphanModel = this.draft.models.find(
      (m) => m.customizable && m.templateId && !m.masterDesignId && this.draft.rows.some((r) => r.modelId === m.modelId),
    );
    if (orphanModel) return `Crée d'abord le design de base de « ${orphanModel.title} » (clique sur son onglet).`;
    const modeMissing = this.draft.models.find((m) => this.#needsMode(m) && this.draft.rows.some((r) => r.modelId === m.modelId));
    if (modeMissing) return `Indique d'abord pour « ${modeMissing.title} » si toutes les paires sont identiques ou personnalisées.`;
    const unavailable = this.draft.models.find((m) => !this.cards.get(m.productId) && this.draft.rows.some((r) => r.modelId === m.modelId));
    if (unavailable) return `« ${unavailable.title} » n'est plus disponible : retire ce modèle ou choisis-en un autre.`;
    const fieldsMissing = this.draft.models.find((m) => this.#fieldsMissing(m) && this.draft.rows.some((r) => r.modelId === m.modelId));
    if (fieldsMissing) return `Les champs de « ${fieldsMissing.title} » n'ont pas pu être chargés : réessaie avant d'ajouter au panier.`;
    const missing = this.draft.rows.filter((row) => !row.taille).length;
    if (missing) return `Choisis une taille sur ${missing} ligne${missing > 1 ? 's' : ''} pour débloquer l'ajout au panier.`;
    if (this.#totals().missingPrice) return 'Une combinaison taille/gamme est indisponible sur certaines lignes.';
    return null;
  }

  #highlightMissingTailles() {
    let first = null;
    for (const select of this.refs['table-body']?.querySelectorAll('select[data-kind="taille"]') ?? []) {
      const missing = !select.value;
      select.classList.toggle('is-missing', missing);
      if (missing && !first) first = select;
    }
    first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #showError(message, duration = 6000) {
    clearTimeout(this.errorTimer);
    this.refs['group-error'].textContent = message;
    this.refs['group-error'].hidden = false;
    this.refs['group-error'].scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    this.errorTimer = setTimeout(() => (this.refs['group-error'].hidden = true), duration);
  }

  #setProgress(done, total, label) {
    this.refs['progress'].hidden = false;
    this.refs['progress-fill'].style.width = `${Math.round((done / total) * 100)}%`;
    this.refs['progress-label'].textContent = label;
  }

  // ——————————————————————————————————————————————— panier ———

  /** fetch avec reprise sur 429/5xx/réseau (backoff 1 s, 2 s, 4 s). */
  async #fetchWithRetry(url, options, attempts = 4) {
    let lastError = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const response = await fetch(url, options);
        if (response.status !== 429 && response.status < 500) return response;
        lastError = new Error(`HTTP ${response.status}`);
      } catch (error) {
        lastError = error;
      }
      if (attempt < attempts - 1) await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt));
    }
    throw lastError ?? new Error('réseau indisponible');
  }

  /** Libellé lisible d'une ligne pour les messages : « ligne 3 (LÉA) ». */
  #rowLabel(row) {
    const model = this.draft.models.find((m) => m.modelId === row.modelId);
    const index = this.draft.rows.filter((r) => r.modelId === row.modelId).findIndex((r) => r.rowId === row.rowId) + 1;
    const values = this.#effectiveValues(model, row);
    const member = this.#fieldsFor(model)
      .map((f) => values[this.#fieldKey(f)])
      .filter(Boolean)
      .join(' · ');
    return `ligne ${index}${member ? ` (${member})` : ''}`;
  }

  #highlightRow(rowId) {
    const cell = this.querySelector(`[data-row-id="${this.#escape(rowId)}"]`);
    if (!cell) return;
    cell.classList.add('is-error');
    cell.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setTimeout(() => cell.classList.remove('is-error'), 8000);
  }

  async #addToCart() {
    if (this.busy) return;
    const button = this.refs['add-to-cart'];
    // Tableau figé pendant toute l'opération : une frappe remettait designId à
    // null sur une ligne déjà déclinée (paire ajoutée sans design) et
    // réactivait le bouton (double ajout au panier).
    this.busy = true;
    button.disabled = true;
    button.classList.add('is-loading');
    this.refs['group-error'].hidden = true;
    this.refs['table-body'].closest('table')?.classList.add('is-busy');
    this.refs['add-row'].disabled = true;
    this.refs['save-later'].disabled = true;

    try {
      const blocking = this.#blockingReason();
      if (blocking) throw new Error(blocking);

      const groupId = await this.#ensureBackendDraft();
      await this.#pushBackendDraft();

      // 1. Matérialisation des lignes personnalisables (par lots, avec progression)
      const toMaterialize = this.draft.rows.filter((row) => {
        const model = this.draft.models.find((m) => m.modelId === row.modelId);
        return model?.customizable && model.masterDesignId && !row.designId;
      });
      let done = 0;
      for (let index = 0; index < toMaterialize.length; index += MATERIALIZE_CHUNK) {
        const chunk = toMaterialize.slice(index, index + MATERIALIZE_CHUNK);
        this.#setProgress(done, toMaterialize.length || 1, `Préparation des designs… ${done}/${toMaterialize.length}`);
        const response = await this.#fetchWithRetry(`${this.backendBase}/group-order/${groupId}/materialize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rows: chunk.map((row) => {
              const model = this.draft.models.find((m) => m.modelId === row.modelId);
              return {
                rowId: row.rowId,
                templateId: model.templateId,
                masterDesignId: model.masterDesignId,
                values: this.#effectiveValues(model, row),
              };
            }),
          }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          if (payload.rowId) this.#highlightRow(payload.rowId);
          throw new Error(
            payload.rowId
              ? `Impossible de préparer la ${this.#rowLabel(this.draft.rows.find((r) => r.rowId === payload.rowId) ?? chunk[0])} : ${payload.error || 'réessaie'}.`
              : 'Préparation des designs impossible pour le moment : réessaie dans un instant.',
          );
        }
        const data = await response.json();
        const failed = [];
        for (const result of data.results) {
          const row = this.draft.rows.find((r) => r.rowId === result.rowId);
          if (!row) continue;
          if (!result.designId) {
            failed.push({ row, error: result.error });
            continue;
          }
          row.designId = result.designId;
          done += 1;
        }
        // Les designs réussis sont conservés même si une ligne a échoué : le
        // prochain clic ne refait que le reste.
        this.#save();
        if (failed.length) {
          this.#highlightRow(failed[0].row.rowId);
          const detail = failed[0].error && failed[0].error.startsWith('champ inconnu') ? ' Le modèle a changé, recrée le design de base.' : '';
          throw new Error(`Impossible de préparer la ${this.#rowLabel(failed[0].row)}${failed.length > 1 ? ` et ${failed.length - 1} autre(s)` : ''} : réessaie.${detail}`);
        }
      }
      this.#setProgress(1, 1, 'Ajout au panier…');

      // 2. Ajout panier : une ligne par ligne du tableau
      const items = this.draft.rows.map((row) => {
        const model = this.draft.models.find((m) => m.modelId === row.modelId);
        const card = this.cards.get(model.productId);
        const variant = card ? this.#findPairVariant(card.data, row.taille, model.gamme) : null;
        if (!variant) throw new Error(`Variante introuvable pour ${model.title} en ${row.taille} : retire cette ligne.`);
        if (model.customizable && model.masterDesignId && !row.designId) {
          throw new Error(`La ${this.#rowLabel(row)} n'a pas de design : réessaie l'ajout au panier.`);
        }
        const fields = this.#fieldsFor(model);
        const values = this.#effectiveValues(model, row);
        const memberLabel = fields
          .map((f) => values[this.#fieldKey(f)])
          .filter(Boolean)
          .join(' · ');
        const properties = {
          _groupOrderId: groupId,
          _groupOrderRole: 'paire',
        };
        if (row.designId) properties.designId = row.designId;
        if (memberLabel) properties.Membre = memberLabel;
        return { id: variant.id, quantity: row.qty, properties };
      });

      const response = await this.#fetchWithRetry('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      }, 2);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || 'Ajout au panier refusé par la boutique : réessaie.');
      }

      // 3. Brouillon marqué « au panier » (pas « commandé » : seul le paiement
      // Shopify le fera) + notification panier
      await this.#pushBackendDraft({ status: 'in_cart' });
      localStorage.removeItem(DRAFT_KEY);

      const cart = await fetch('/cart.js').then((r) => r.json());
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: cart.item_count, source: 'group-order', sections: null }),
      );
      window.location.href = '/cart';
    } catch (error) {
      this.#showError(error.message || 'Une erreur est survenue, réessaie.', 10000);
    } finally {
      this.busy = false;
      button.classList.remove('is-loading');
      this.refs['progress'].hidden = true;
      this.refs['table-body'].closest('table')?.classList.remove('is-busy');
      this.refs['add-row'].disabled = false;
      this.refs['save-later'].disabled = false;
      this.#renderSummary();
    }
  }

  #escape(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  }
}

if (!customElements.get('group-order-composer')) {
  customElements.define('group-order-composer', GroupOrderComposer);
}
